import { collection, doc, runTransaction, serverTimestamp } from "firebase/firestore";
import { auth, db } from "../firebase";
import { getActiveProfileInTransaction } from "../services/financeCore/clientTaxProfileService";
import { assertPeriodOpen } from "../services/financeCore/periodLockService";
import { InvoiceOverrideRequiredError, buildCorrectionRecord, figureChanges } from "../services/financeCore/invoiceCorrection";
import { buildSnapshotFields, snapshotDocRef } from "../services/financeCore/invoiceSnapshotService";

const MONTHS = { jan: "01", feb: "02", mar: "03", apr: "04", may: "05", jun: "06", jul: "07", aug: "08", sep: "09", oct: "10", nov: "11", dec: "12" };
const round = (value) => Math.round(Number(value || 0));

export function cortevaMonthKey(period) {
  const value = String(period || "").trim();
  let match = value.match(/(jan|feb|mar|apr|may|jun|jul|aug|sep|oct|nov|dec)[a-z]*[- /]*(\d{2,4})/i);
  if (match) {
    const year = match[2].length === 2 ? `20${match[2]}` : match[2];
    return `${year}-${MONTHS[match[1].slice(0, 3).toLowerCase()]}`;
  }
  match = value.match(/(20\d{2})[- /](1[0-2]|0?[1-9])(?!\d)/);
  if (match) return `${match[1]}-${String(match[2]).padStart(2, "0")}`;
  return "";
}

// Segment -> client mapping: IFS FO and the flat-fee Consultant invoice bill
// under Corteva Crop (a separate legal entity from Corteva Agriscience
// sharing the same office); every other segment stays under Corteva.
function clientCodeForSegment(segmentKey) {
  return segmentKey === "ifs" || segmentKey === "consultantCrop" ? "CORTEVA_CROP" : "CORTEVA";
}

const CORTEVA_FIGURES = [
  ["reimbursement", "Reimbursement"],
  ["serviceCharges", "Service charges"],
  ["gstAmount", "GST"],
  ["grossAmount", "Invoice total"]
];

// Approving an invoice number that is already approved: identical figures are a no-op;
// different figures need { force: true, correctionReason } (see runWithCorrectionPrompt),
// which replaces the approved snapshot and logs old vs new. The result says which happened.
export async function approveCortevaInvoice(input, { force = false, correctionReason = "" } = {}) {
  const invoiceNumber = String(input.invoiceNumber || "").trim();
  const monthKey = cortevaMonthKey(input.period);
  if (!invoiceNumber || !input.invoiceDate || !monthKey) throw new Error("Enter a valid invoice number, invoice date, and payroll month.");
  if (!(Number(input.grossAmount) > 0)) throw new Error("The invoice total must be greater than zero.");
  const registryId = encodeURIComponent(invoiceNumber.toUpperCase());
  const invoiceAmount = round(input.invoiceAmount);
  const gstAmount = round(input.gstAmount);
  const grossAmount = round(input.grossAmount);
  const serviceCharges = round(input.serviceCharges);
  const clientCode = clientCodeForSegment(input.segmentKey);
  // The standard 7%/20% withholding assumption only applies to the
  // reimbursement-style segments — Corteva Crop's flat-fee Consultant
  // invoice already states its complete, final tax treatment (4% consultancy
  // tax + 16% GST on the admin charge only) with nothing further withheld.
  const applyStandardWithholding = input.segmentKey !== "consultantCrop";
  const clientWhtDeduction = applyStandardWithholding ? round((serviceCharges + gstAmount) * 0.07) : 0;
  const gstWithheld = applyStandardWithholding ? round(gstAmount * 0.20) : 0;
  const snapshot = {
    invoiceNumber,
    monthKey,
    period: String(input.period).trim(),
    invoiceDate: input.invoiceDate,
    segmentName: input.segmentName || "Corteva",
    segmentKey: input.segmentKey || "corteva",
    headCount: Number(input.headCount || 0),
    reimbursement: round(input.reimbursement),
    reimbursementBreakdown: input.reimbursementBreakdown || null,
    serviceCharges,
    invoiceAmount,
    gstAmount,
    grossAmount,
    clientWhtDeduction,
    gstWithheld,
    expectedReceipt: grossAmount - clientWhtDeduction - gstWithheld,
    status: "APPROVED",
    approvedBy: auth.currentUser?.uid || null,
    approvedByEmail: auth.currentUser?.email || null,
    approvedAtIso: new Date().toISOString()
  };

  let outcome = "approved";
  await assertPeriodOpen(monthKey);
  await runTransaction(db, async (transaction) => {
    outcome = "approved";
    const registryRef = doc(db, "corteva_invoice_registry", registryId);
    const workspaceRef = doc(db, "corteva_monthly_invoice_workspaces", monthKey);
    const [registryDoc, workspaceDoc] = await Promise.all([transaction.get(registryRef), transaction.get(workspaceRef)]);
    let correctionRecord = null;
    let finalInvoiceDate = input.invoiceDate;
    if (registryDoc.exists()) {
      const existing = registryDoc.data();
      if (existing.monthKey !== monthKey || existing.segmentKey !== snapshot.segmentKey) {
        throw new Error(`${invoiceNumber} is already approved for ${existing.segmentName || "another Corteva invoice"} in ${existing.monthKey || "another month"}.`);
      }
      const changes = figureChanges(CORTEVA_FIGURES, existing, snapshot);
      if (!changes.length) {
        outcome = "unchanged";
        return;
      }
      if (!force) {
        throw new InvoiceOverrideRequiredError({ invoiceNumber, existingAmount: existing.grossAmount, newAmount: grossAmount, changes });
      }
      // The invoice keeps the date it was first approved with, so receivable ageing does not move.
      finalInvoiceDate = existing.invoiceDate || input.invoiceDate;
      correctionRecord = buildCorrectionRecord({
        invoiceNumber, clientCode, monthKey, previousAmount: existing.grossAmount, newAmount: grossAmount,
        changes, reason: correctionReason, user: auth.currentUser, correctedAt: serverTimestamp()
      });
      outcome = "corrected";
    }
    const workspace = workspaceDoc.exists() ? workspaceDoc.data() : {};
    const taxProfile = await getActiveProfileInTransaction(transaction, clientCode);
    const snapshotFields = buildSnapshotFields({
      clientCode,
      sourceCollection: "corteva_invoice_registry",
      sourceDocId: registryId,
      invoiceNumber,
      monthKey,
      accountingPeriod: monthKey,
      invoiceDate: finalInvoiceDate,
      taxProfile,
      sourceDoc: {
        segmentKey: snapshot.segmentKey,
        reimbursement: snapshot.reimbursement, reimbursementBreakdown: snapshot.reimbursementBreakdown,
        serviceCharges, invoiceAmount, gstAmount, grossAmount,
        consultancyFee: input.consultancyFee, consultancyTax: input.consultancyTax,
        serviceAdminCharge: input.serviceAdminCharge, gstOnAdmin: input.gstOnAdmin
      }
    });

    const stored = { ...snapshot, invoiceDate: finalInvoiceDate };
    transaction.set(registryRef, {
      ...stored,
      ...(correctionRecord ? { correctionReason: correctionRecord.reason, correctedAtIso: new Date().toISOString() } : {}),
      approvedAt: registryDoc.exists() ? registryDoc.data().approvedAt : serverTimestamp(),
      updatedAt: serverTimestamp()
    }, { merge: true });
    transaction.set(workspaceRef, {
      monthKey,
      invoiceReconciliation: { ...(workspace.invoiceReconciliation || {}), [registryId]: stored },
      updatedAt: serverTimestamp(),
      updatedBy: auth.currentUser?.uid || null
    }, { merge: true });
    if (correctionRecord) {
      // Marks the write as an authorized correction: the snapshot rule only lets an
      // approved snapshot change when a reason is present.
      snapshotFields.correctionReason = correctionRecord.reason;
      transaction.set(snapshotDocRef(registryId), snapshotFields, { merge: true });
      transaction.set(doc(collection(db, "invoice_snapshot_corrections")), correctionRecord);
    } else {
      transaction.set(snapshotDocRef(registryId), snapshotFields);
    }
  });
  return { ...snapshot, corrected: outcome === "corrected", unchanged: outcome === "unchanged" };
}
